import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

import { sanitizeRecord, validateVmtRecord, pickString } from '../utils/vmt';

const STORAGE_KEY = 'bulk-send-staging-records';

const getBrowserStorage = () => {
  if (typeof window === 'undefined') return null;
  const storage = window.localStorage;
  if (!storage) return null;
  if (typeof storage.getItem !== 'function' || typeof storage.setItem !== 'function') return null;
  return storage;
};

export const BULK_SEND_UPLOAD_STATUSES = {
  NOT_SENT: 'NOT SENT',
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
};

const generateId = (referenceId) => {
  if (referenceId) return `staged-${referenceId}`;
  if (typeof crypto === 'object' && typeof crypto.randomUUID === 'function') {
    return `staged-${crypto.randomUUID()}`;
  }
  return `staged-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
};

const INFO_ONLY_FIELDS = ['dealerName', 'brands'];

const normalizeInfoFieldValue = (value) => {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value.trim() : String(value).trim();
};

const normalizeInfoFields = (fields = null) => {
  if (!fields || typeof fields !== 'object') return null;
  const dealerName = normalizeInfoFieldValue(fields.dealerName ?? '');
  const brands = normalizeInfoFieldValue(fields.brands ?? '');
  if (!dealerName && !brands) return null;
  return { dealerName, brands };
};

const removeInfoOnlyKeys = (record = {}) => {
  const next = { ...record };
  INFO_ONLY_FIELDS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(next, key)) {
      delete next[key];
    }
  });
  return next;
};

const resolveStagePayloadRecord = (payload) => {
  if (!payload) return { record: {}, infoFields: null };
  if (typeof payload === 'object' && payload !== null && Object.prototype.hasOwnProperty.call(payload, 'record')) {
    return {
      record: payload.record,
      infoFields: payload.infoFields ?? null,
    };
  }
  return { record: payload, infoFields: null };
};

const buildEntry = (record, options = {}) => {
  const sanitized = sanitizeRecord({
    ...record,
    notes: record.notes ?? '',
    programName: record.programName ?? '',
  });
  const normalizedProgramName = pickString([
    record.programName,
    record.programLabel,
    record.program,
    record.categoryName,
  ]);
  if (normalizedProgramName) {
    sanitized.programName = normalizedProgramName;
  }
  const recordToStore = removeInfoOnlyKeys(sanitized);
  return {
    id: generateId(recordToStore.referenceId),
    record: recordToStore,
    errors: validateVmtRecord(recordToStore),
    stagedAt: Date.now(),
    uploadStatus: BULK_SEND_UPLOAD_STATUSES.NOT_SENT,
    auditDetails: null,
    dedupeReferenceId:
      options.allowDuplicate || !recordToStore.referenceId ? null : recordToStore.referenceId,
    infoFields: normalizeInfoFields(options.infoFields ?? null),
  };
};

const loadStoredRecords = () => {
  const storage = getBrowserStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((entry) => ({
      id: entry.id || generateId(entry?.record?.referenceId),
      record: sanitizeRecord(entry.record || {}),
      errors: validateVmtRecord(entry.record || {}),
      stagedAt: entry.stagedAt || Date.now(),
      uploadStatus: entry.uploadStatus || BULK_SEND_UPLOAD_STATUSES.NOT_SENT,
      auditDetails: entry.auditDetails ?? null,
      infoFields: normalizeInfoFields(entry.infoFields ?? null),
    }));
  } catch (error) {
    console.warn('Failed to load bulk send staging records', error);
    return [];
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'stage': {
      const entry = buildEntry(action.payload.record, { infoFields: action.payload.infoFields });
      const referenceId = entry.record.referenceId;
      if (entry.dedupeReferenceId && referenceId) {
        const existingIndex = state.findIndex((item) => item.record.referenceId === referenceId);
        if (existingIndex !== -1) {
          const next = [...state];
          next[existingIndex] = entry;
          return next;
        }
      }
      return [...state, entry];
    }
    case 'stageDuplicate': {
      const entry = buildEntry(action.payload.record, {
        allowDuplicate: true,
        infoFields: action.payload.infoFields,
      });
      return [...state, entry];
    }
    case 'stageMultiple': {
      const next = [...state];
      action.payload.records.forEach((payloadRecord) => {
        const { record: recordToStage, infoFields } = resolveStagePayloadRecord(payloadRecord);
        const entry = buildEntry(recordToStage, { infoFields });
        const referenceId = entry.record.referenceId;
        if (referenceId) {
          const existingIndex = next.findIndex((item) => item.record.referenceId === referenceId);
          if (existingIndex !== -1) {
            next[existingIndex] = entry;
            return;
          }
        }
        next.push(entry);
      });
      return next;
    }
    case 'update': {
      const { id, updates } = action.payload;
      return state.map((entry) => {
        if (entry.id !== id) return entry;
        const merged = { ...entry.record, ...updates };
        const sanitized = sanitizeRecord(merged);
        return {
          ...entry,
          record: sanitized,
          errors: validateVmtRecord(sanitized),
          stagedAt: Date.now(),
        };
      });
    }
    case 'batchUpdate': {
      const { ids, updates } = action.payload;
      if (!Array.isArray(ids) || ids.length === 0) return state;
      return state.map((entry) => {
        if (!ids.includes(entry.id)) return entry;
        const merged = { ...entry.record, ...updates };
        const sanitized = sanitizeRecord(merged);
        return {
          ...entry,
          record: sanitized,
          errors: validateVmtRecord(sanitized),
          stagedAt: Date.now(),
        };
      });
    }
    case 'remove': {
      return state.filter((entry) => entry.id !== action.payload.id);
    }
    case 'clear': {
      return [];
    }
    case 'updateUploadMetadata': {
      const { ids, uploadStatus, auditDetails } = action.payload;
      if (!Array.isArray(ids) || ids.length === 0) {
        return state;
      }
      const hasAuditDetails = Object.prototype.hasOwnProperty.call(action.payload, 'auditDetails');
      return state.map((entry) => {
        if (!ids.includes(entry.id)) return entry;
        return {
          ...entry,
          uploadStatus: uploadStatus ?? entry.uploadStatus,
          auditDetails: hasAuditDetails ? auditDetails : entry.auditDetails,
        };
      });
    }
    default:
      return state;
  }
};

const BulkSendStagingContext = createContext(null);

export const BulkSendStagingProvider = ({ children }) => {
  const [records, dispatch] = useReducer(reducer, [], loadStoredRecords);

  useEffect(() => {
    const storage = getBrowserStorage();
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch (error) {
      console.error('Failed to persist bulk send staging records', error);
    }
  }, [records]);

  const stageRecord = useCallback((record, infoFields = null) => dispatch({ type: 'stage', payload: { record, infoFields } }), []);
  const stageRecords = useCallback((recordsToStage) => dispatch({ type: 'stageMultiple', payload: { records: recordsToStage } }), []);
  const stageDuplicateRecord = useCallback(
    (record, infoFields = null) => dispatch({ type: 'stageDuplicate', payload: { record, infoFields } }),
    []
  );
  const updateRecord = useCallback((id, updates) => dispatch({ type: 'update', payload: { id, updates } }), []);
  const batchUpdateRecords = useCallback((ids, updates) => dispatch({ type: 'batchUpdate', payload: { ids, updates } }), []);
  const removeRecord = useCallback((id) => dispatch({ type: 'remove', payload: { id } }), []);
  const clearRecords = useCallback(() => dispatch({ type: 'clear' }), []);
  const updateUploadMetadata = useCallback(
    (payload) => dispatch({ type: 'updateUploadMetadata', payload }),
    []
  );

  return (
    <BulkSendStagingContext.Provider
      value={{
        records,
        stageRecord,
        stageRecords,
        updateRecord,
        batchUpdateRecords,
        removeRecord,
        clearRecords,
        stageDuplicateRecord,
        updateUploadMetadata,
      }}
    >
      {children}
    </BulkSendStagingContext.Provider>
  );
};

export const useBulkSendStaging = () => {
  const context = useContext(BulkSendStagingContext);
  if (!context) {
    throw new Error('useBulkSendStaging must be used within a BulkSendStagingProvider');
  }
  return context;
};
